import { useState, useCallback } from "react"
import type { DiscoverFilters } from "~/react/types/discover"

interface UseDiscoverFiltersReturn {
  filters: DiscoverFilters
  setFilters: React.Dispatch<React.SetStateAction<DiscoverFilters>>
  updateFilter: <K extends keyof DiscoverFilters>(key: K, value: DiscoverFilters[K]) => void
  clearFilter: (key: keyof DiscoverFilters) => void
  clearAllFilters: () => void
  hasFilters: boolean
}

export function useDiscoverFilters(initialFilters: DiscoverFilters = {}): UseDiscoverFiltersReturn {
  const [filters, setFilters] = useState<DiscoverFilters>(initialFilters)

  const updateFilter = useCallback(<K extends keyof DiscoverFilters>(key: K, value: DiscoverFilters[K]) => {
    setFilters((prev) => {
      const newFilters = { ...prev }
      // Remove key if value is empty (null, undefined, empty array, empty string)
      const isEmpty =
        value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)
      if (isEmpty) {
        delete newFilters[key]
      } else {
        newFilters[key] = value
      }
      return newFilters
    })
  }, [])

  const clearFilter = useCallback((key: keyof DiscoverFilters) => {
    setFilters((prev) => {
      const newFilters = { ...prev }
      delete newFilters[key]
      return newFilters
    })
  }, [])

  const clearAllFilters = useCallback(() => {
    setFilters({})
  }, [])

  // Check if there are any meaningful filter values
  const hasFilters = Object.entries(filters).some(([, value]) => {
    if (value === null || value === undefined || value === "") return false
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === "object") return Object.keys(value).length > 0
    return true
  })

  return {
    filters,
    setFilters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
  }
}
